// src/pages/ProjectDashboardPage.tsx
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    DashboardApiResponse,
    ProjectDashboardResponse,
    DashboardFlag,
    ProjectSummary,
} from "../types/dashboard";
import { AppLayout } from "../components/layout/AppLayout";
import { api } from "../lib/apiClient";
import { useAuth } from "../auth/AuthContext";
import { hasApiBaseUrl } from "../lib/orgRuntime";

type ProjectsApiResponse = {
    ok: boolean;
    projects?: ProjectSummary[];
    error?: string;
};

export const ProjectDashboardPage: React.FC = () => {
    const { orgId } = useAuth();
    const canUseOrgScopedApi = hasApiBaseUrl && !!orgId;

    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [projects, setProjects] = useState<ProjectSummary[]>([]);

    const initialProjectId =
        searchParams.get("projectId") ?? projects[0]?.id ?? null;

    const [currentProjectId, setCurrentProjectId] = useState<string | null>(
        initialProjectId,
    );
    const [dashboard, setDashboard] = useState<ProjectDashboardResponse | null>(
        null,
    );
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [refreshToken, setRefreshToken] = useState(0);

    // Load real projects list (same pattern as Sources page)
    useEffect(() => {
        const loadProjects = async () => {
            // If API / org not wired, stay on demo project
            if (!canUseOrgScopedApi) {
                setProjects([]);
                return;
            }

            try {
                const json: ProjectsApiResponse = await api.get("/projects");

                const loaded = json.projects ?? [];
                if (loaded.length > 0) {
                    setProjects(loaded);

                    // If no project selected yet, default to first real project
                    if (!currentProjectId) {
                        const firstId = loaded[0]?.id ?? null;
                        if (firstId) {
                            setCurrentProjectId(firstId);
                            setSearchParams({ projectId: firstId });
                        }
                    }
                } else {
                    setProjects([]);
                }
            } catch (err) {
                console.error("Failed to load projects", err);
                if (projects.length === 0) setProjects([]);
            }
        };

        void loadProjects();
    }, [canUseOrgScopedApi]);

    // Keep URL in sync when project changes
    const handleProjectChange = (projectId: string) => {
        setCurrentProjectId(projectId);
        setSearchParams({ projectId });
    };

    const handleFlagAction = async (
        flag: DashboardFlag,
        action: "dismiss" | "reinstate",
    ) => {
        if (!flag.id) return;

        const endpoint =
            action === "dismiss"
                ? `/flags/${flag.id}/dismiss`
                : `/flags/${flag.id}/reinstate`;

        try {
            await api.post(endpoint, {
                projectId: currentProjectId,
            });

            // Trigger a dashboard refresh
            setRefreshToken((t) => t + 1);
        } catch (err) {
            console.error("Failed to update flag status", err);
        }
    };

    useEffect(() => {
        if (!canUseOrgScopedApi || !currentProjectId) return;

        const fetchDashboard = async () => {
            setLoading(true);
            setError(null);
            try {
                const json: DashboardApiResponse = await api.get(
                    `/projects/${currentProjectId}/dashboard`,
                );
                if (!json.ok) throw new Error("Dashboard request was not ok");
                setDashboard(json.dashboard);
            } catch (err) {
                console.error(err);
                setError("Could not load dashboard.");
            } finally {
                setLoading(false);
            }
        };

        void fetchDashboard();
    }, [currentProjectId, refreshToken, canUseOrgScopedApi]);

    return (
        <AppLayout
            projects={projects}
            currentProjectId={currentProjectId}
            onProjectChange={handleProjectChange}
        >
            <div className="px-8 py-6 space-y-6">
                {/* Header */}
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-semibold text-slate-50">
                            Dashboard
                        </h1>
                        <p className="text-sm text-slate-400">
                            Project overview • scope creep at a glance
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={() => navigate("/workbench")}
                            className="rounded-2xl border border-slate-700 px-4 py-2 text-xs font-semibold text-slate-100 bg-slate-900/80 hover:bg-slate-800/90 transition"
                        >
                            Open Workbench
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate("/sources")}
                            className="rounded-2xl px-4 py-2 text-xs font-semibold text-slate-900 bg-gradient-to-r from-sky-400 via-indigo-400 to-violet-500 shadow-lg shadow-sky-500/40 hover:brightness-110 transition"
                        >
                            Upload &amp; Analyze
                        </button>
                    </div>
                </div>

                {/* Loading / error */}
                {!hasApiBaseUrl && (
                    <div className="text-sm text-red-200 bg-red-950/40 border border-red-700/60 rounded-xl px-4 py-3">
                        Missing API configuration: VITE_API_BASE_URL.
                    </div>
                )}
                {hasApiBaseUrl && !orgId && (
                    <div className="text-sm text-amber-200 bg-amber-950/40 border border-amber-700/60 rounded-xl px-4 py-3">
                        Select or create an organization to ingest and analyze documents.
                    </div>
                )}
                {loading && (
                    <div className="text-sm text-slate-400">Loading dashboard…</div>
                )}
                {error && (
                    <div className="text-sm text-rose-300 bg-rose-950/40 border border-rose-700/60 rounded-xl px-4 py-3">
                        {error}
                    </div>
                )}

                {/* Content */}
                {dashboard && !loading && !error && (
                    <>
                        <KpiRow dashboard={dashboard} />
                        <RecentFlagsCard
                            flags={dashboard.recentFlags}
                            onFlagAction={handleFlagAction}
                        />
                        <div className="text-[11px] text-slate-500 pt-2">
                            Demo snapshot frozen • metrics reflect current open flags
                        </div>
                    </>
                )}
            </div>
        </AppLayout>
    );
};

interface KpiRowProps {
    dashboard: ProjectDashboardResponse;
}

const formatCurrency = (amount: number): string => {
    if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1)}M`;
    if (amount >= 1_000) return `$${(amount / 1_000).toFixed(1)}k`;
    return `$${amount.toLocaleString()}`;
};

const KpiRow: React.FC<KpiRowProps> = ({ dashboard }) => {
    const { flagsOpen, flagsTotal, flagsNew } = dashboard.totals;
    const { revenueAtRisk, creepVelocity, severityBreakdown, evidenceStrength } =
        dashboard;

    // --- Revenue at Risk badge ---
    const revLevel =
        revenueAtRisk.totalAmount >= 50_000
            ? "HIGH"
            : revenueAtRisk.totalAmount > 0
                ? "RECOVERABLE"
                : "NONE";
    const revBadgeColor =
        revLevel === "HIGH"
            ? "bg-rose-500/10 text-rose-300 border border-rose-500/60"
            : revLevel === "RECOVERABLE"
                ? "bg-amber-500/10 text-amber-300 border border-amber-500/60"
                : "bg-emerald-500/10 text-emerald-300 border border-emerald-500/60";

    // --- Creep Velocity badge ---
    const velLabel =
        creepVelocity.trend === "up"
            ? "ACCELERATING"
            : creepVelocity.trend === "down"
                ? "SLOWING"
                : "STABLE";
    const velBadgeColor =
        creepVelocity.trend === "up"
            ? "bg-rose-500/10 text-rose-300 border border-rose-500/60"
            : creepVelocity.trend === "down"
                ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/60"
                : "bg-slate-700/60 text-slate-200 border border-slate-500/80";
    const trendArrow =
        creepVelocity.trend === "up"
            ? "\u2191"
            : creepVelocity.trend === "down"
                ? "\u2193"
                : "\u2192";

    // --- Open Flags badge ---
    const openLevel =
        flagsOpen >= 10 ? "ACTION NEEDED" : flagsOpen > 0 ? "WATCHING" : "CLEAR";
    const openBadgeColor =
        openLevel === "ACTION NEEDED"
            ? "bg-rose-500/10 text-rose-300 border border-rose-500/60"
            : openLevel === "WATCHING"
                ? "bg-amber-500/10 text-amber-300 border border-amber-500/60"
                : "bg-emerald-500/10 text-emerald-300 border border-emerald-500/60";

    // --- Evidence Strength badge ---
    const strengthLevel =
        evidenceStrength.strengthPercent >= 70
            ? "STRONG"
            : evidenceStrength.strengthPercent >= 40
                ? "MODERATE"
                : "WEAK";
    const strengthBadgeColor =
        strengthLevel === "STRONG"
            ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/60"
            : strengthLevel === "MODERATE"
                ? "bg-amber-500/10 text-amber-300 border border-amber-500/60"
                : "bg-rose-500/10 text-rose-300 border border-rose-500/60";

    // --- Severity Breakdown ---
    const sevTotal = severityBreakdown.high + severityBreakdown.medium + severityBreakdown.low;

    return (
        <div className="space-y-4">
            {/* Row 1: Revenue Recovery */}
            <div>
                <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-[0.2em] mb-2 ml-1">
                    Revenue Recovery
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Revenue at Risk */}
                    <KpiCard
                        title="Revenue at risk"
                        subtitle="Estimated cost of open flags"
                        badge={revLevel}
                        badgeClassName={revBadgeColor}
                    >
                        <div className="text-4xl font-semibold text-slate-50">
                            {revenueAtRisk.totalAmount > 0
                                ? formatCurrency(revenueAtRisk.totalAmount)
                                : "$0"}
                        </div>
                        <div className="text-xs text-slate-400 mt-1">
                            {revenueAtRisk.flagsWithCost} costed
                            {revenueAtRisk.flagsWithoutCost > 0 && (
                                <span className="text-amber-400">
                                    {" "}&bull; {revenueAtRisk.flagsWithoutCost} uncosted
                                </span>
                            )}
                        </div>
                    </KpiCard>

                    {/* Creep Velocity */}
                    <KpiCard
                        title="Creep velocity"
                        subtitle="New flags per week"
                        badge={velLabel}
                        badgeClassName={velBadgeColor}
                    >
                        <div className="flex items-baseline gap-2">
                            <span className="text-4xl font-semibold text-slate-50">
                                {creepVelocity.thisWeek}
                            </span>
                            <span className="text-lg text-slate-500">
                                {trendArrow}
                            </span>
                        </div>
                        <div className="text-xs text-slate-400 mt-1">
                            vs. {creepVelocity.lastWeek} last week
                            {creepVelocity.changePercent !== 0 && (
                                <span
                                    className={
                                        creepVelocity.trend === "up"
                                            ? " text-rose-400"
                                            : " text-emerald-400"
                                    }
                                >
                                    {" "}({creepVelocity.changePercent > 0 ? "+" : ""}
                                    {creepVelocity.changePercent}%)
                                </span>
                            )}
                        </div>
                    </KpiCard>

                    {/* Open Flags (kept) */}
                    <KpiCard
                        title="Open flags"
                        subtitle="Needs decision"
                        badge={openLevel}
                        badgeClassName={openBadgeColor}
                    >
                        <div className="text-4xl font-semibold text-slate-50">
                            {flagsOpen}
                        </div>
                        <div className="text-xs text-slate-400 mt-1">
                            {flagsTotal} total &bull; {flagsNew} new this month
                        </div>
                    </KpiCard>
                </div>
            </div>

            {/* Row 2: Scope Intelligence */}
            <div>
                <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-[0.2em] mb-2 ml-1">
                    Scope Intelligence
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Severity Breakdown */}
                    <KpiCard
                        title="Severity breakdown"
                        subtitle="Open flags by priority"
                    >
                        <div className="flex items-end gap-3 mt-1">
                            <SeverityBar
                                label="High"
                                count={severityBreakdown.high}
                                total={sevTotal}
                                color="bg-rose-500"
                            />
                            <SeverityBar
                                label="Med"
                                count={severityBreakdown.medium}
                                total={sevTotal}
                                color="bg-amber-500"
                            />
                            <SeverityBar
                                label="Low"
                                count={severityBreakdown.low}
                                total={sevTotal}
                                color="bg-emerald-500"
                            />
                        </div>
                    </KpiCard>

                    {/* Evidence Strength */}
                    <KpiCard
                        title="Evidence strength"
                        subtitle="Baseline + comparison coverage"
                        badge={strengthLevel}
                        badgeClassName={strengthBadgeColor}
                    >
                        <div className="text-4xl font-semibold text-slate-50">
                            {evidenceStrength.strengthPercent}%
                        </div>
                        <div className="text-xs text-slate-400 mt-1">
                            {evidenceStrength.strongFlags} strong &bull;{" "}
                            {evidenceStrength.weakFlags} partial
                            {evidenceStrength.noEvidenceFlags > 0 && (
                                <span className="text-rose-400">
                                    {" "}&bull; {evidenceStrength.noEvidenceFlags} missing
                                </span>
                            )}
                        </div>
                    </KpiCard>

                    {/* Scope Classification */}
                    <KpiCard
                        title="Documents analyzed"
                        subtitle="Evidence items processed"
                    >
                        <div className="text-4xl font-semibold text-slate-50">
                            {dashboard.totals.evidenceTotal}
                        </div>
                        <div className="text-xs text-slate-400 mt-1">
                            {dashboard.totals.evidenceFlagged} flagged &bull;{" "}
                            {Math.round((dashboard.totals.detectionRate || 0) * 100)}% detection rate
                        </div>
                    </KpiCard>
                </div>
            </div>
        </div>
    );
};

interface SeverityBarProps {
    label: string;
    count: number;
    total: number;
    color: string;
}

const SeverityBar: React.FC<SeverityBarProps> = ({ label, count, total, color }) => {
    const pct = total > 0 ? (count / total) * 100 : 0;
    const barHeight = total > 0 ? Math.max(8, (count / total) * 64) : 8;

    return (
        <div className="flex flex-col items-center gap-1 flex-1">
            <span className="text-lg font-semibold text-slate-50">{count}</span>
            <div className="w-full rounded-full overflow-hidden bg-slate-800" style={{ height: `${barHeight}px` }}>
                <div className={`h-full ${color} rounded-full`} style={{ width: "100%" }} />
            </div>
            <span className="text-[10px] text-slate-500">{label}</span>
        </div>
    );
};

interface KpiCardProps {
    title: string;
    subtitle: string;
    badge?: string;
    badgeClassName?: string;
    children: React.ReactNode;
}

const KpiCard: React.FC<KpiCardProps> = ({
    title,
    subtitle,
    badge,
    badgeClassName = "",
    children,
}) => {
    return (
        <div className="relative rounded-3xl bg-slate-900/80 border border-slate-700/70 px-5 py-4 shadow-[0_18px_40px_-24px_rgba(15,23,42,1)] overflow-hidden">
            {/* subtle gradient accent */}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-sky-500/5 via-transparent to-violet-500/10" />
            <div className="relative flex flex-col gap-2">
                <div className="flex items-start justify-between">
                    <div>
                        <div className="text-xs font-semibold text-slate-400 uppercase tracking-[0.18em]">
                            {title}
                        </div>
                        <div className="text-[11px] text-slate-500">{subtitle}</div>
                    </div>
                    {badge && (
                        <span
                            className={
                                "px-2 py-1 rounded-full text-[10px] font-semibold uppercase tracking-[0.14em] " +
                                badgeClassName
                            }
                        >
                            {badge}
                        </span>
                    )}
                </div>
                {children}
            </div>
        </div>
    );
};

interface RecentFlagsCardProps {
    flags: DashboardFlag[];
    onFlagAction?: (flag: DashboardFlag, action: "dismiss" | "reinstate") => void;
}

const RecentFlagsCard: React.FC<RecentFlagsCardProps> = ({
    flags,
    onFlagAction,
}) => {
    return (
        <div className="mt-4 rounded-3xl bg-slate-950/90 border border-slate-800 overflow-hidden shadow-[0_28px_60px_-30px_rgba(15,23,42,1)]">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-3 bg-slate-950/95 border-b border-slate-800">
                <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-sky-400 shadow-[0_0_12px_rgba(56,189,248,.9)]" />
                    <span className="text-xs font-semibold text-slate-100 tracking-wide">
                        Recent flags
                    </span>
                </div>
                <button className="text-[11px] text-sky-300 hover:text-sky-200 font-medium">
                    View all →
                </button>
            </div>

            {/* Table */}
            <div className="min-h-[220px]">
                {flags.length === 0 ? (
                    <div className="px-6 py-8 text-sm text-slate-500">
                        No flags yet. Upload meeting notes, scopes, or email threads to
                        start catching creep.
                    </div>
                ) : (
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="text-[11px] text-slate-500 bg-slate-950/90 border-b border-slate-800">
                                <th className="font-medium text-left px-6 py-2.5">Flag</th>
                                <th className="font-medium text-left px-4 py-2.5">
                                    Severity
                                </th>
                                <th className="font-medium text-left px-4 py-2.5">
                                    Scope snip
                                </th>
                                <th className="font-medium text-left px-4 py-2.5">
                                    Evidence
                                </th>
                                <th className="font-medium text-left px-4 py-2.5">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {flags.map((flag) => (
                                <FlagRow key={flag.id} flag={flag} onAction={onFlagAction} />
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

interface FlagRowProps {
    flag: DashboardFlag;
    onAction?: (flag: DashboardFlag, action: "dismiss" | "reinstate") => void;
}

const FlagRow: React.FC<FlagRowProps> = ({ flag, onAction }) => {
    const created = new Date(flag.createdAt);

    const severityLabel = (flag.severity ?? "").toLowerCase();

    let badgeClass =
        "bg-slate-700/40 text-slate-200 border border-slate-500/60";
    if (severityLabel === "high") {
        badgeClass =
            "bg-rose-500/10 text-rose-200 border border-rose-500/70";
    } else if (severityLabel === "medium") {
        badgeClass =
            "bg-amber-500/10 text-amber-100 border border-amber-500/70";
    } else if (severityLabel === "low") {
        badgeClass =
            "bg-emerald-500/10 text-emerald-100 border border-emerald-500/70";
    }

    return (
        <tr className="border-b border-slate-800/70 hover:bg-slate-900/60 transition">
            <td className="px-6 py-3 align-top">
                <div className="text-[13px] font-medium text-slate-100">
                    {flag.title ?? "Untitled flag"}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                    {created.toLocaleDateString()}{" "}
                    {created.toLocaleTimeString([], {
                        hour: "numeric",
                        minute: "2-digit",
                    })}
                </div>
            </td>
            <td className="px-4 py-3 align-top">
                <span
                    className={
                        "inline-flex items-center px-2 py-1 rounded-full text-[10px] font-semibold capitalize " +
                        badgeClass
                    }
                >
                    {severityLabel || "n/a"}
                </span>
            </td>
            <td className="px-4 py-3 align-top text-[11px] text-slate-200">
                {flag.summary ?? "No scope summary available."}
            </td>
            <td className="px-4 py-3 align-top text-[11px] text-slate-300">
                {flag.snippet ??
                    flag.document?.title ??
                    "No evidence snippet available."}
            </td>
            <td className="px-4 py-3 align-top text-[11px] text-slate-300">
                <button
                    type="button"
                    onClick={() =>
                        onAction?.(
                            flag,
                            flag.status === "open" ? "dismiss" : "reinstate",
                        )
                    }
                    className="inline-flex items-center rounded-full border border-slate-600/70 bg-slate-900/60 px-3 py-1 text-[11px] font-medium text-slate-100 hover:bg-slate-800 hover:border-slate-400/80 transition"
                >
                    {flag.status === "open" ? "Dismiss" : "Reinstate"}
                </button>
            </td>
        </tr>
    );
};
